import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_SPARKLINES, SPARKLINE_TYP } from "../makros";

export const lektionSparklines: Lesson = {
  id: "sparklines",
  title: "Sparklines: das Diagramm in der Zelle",
  summary: "Ein Verlauf pro Zeile, direkt neben den Zahlen – ohne ein einziges Diagrammobjekt.",
  durationMinutes: 9,
  learningGoals: [
    "Sparklines für mehrere Zeilen auf einmal einfügen",
    "Linien-, Säulen- und Gewinn/Verlust-Sparklines unterscheiden",
    "Höchst- und Tiefstwert markieren",
  ],
  steps: [
    {
      id: "einfuegen",
      title: "Sparklines einfügen",
      explanation: [
        "Sparklines sind Minidiagramme in einer Zelle. Neben einer Zeile mit zwölf " +
          "Monatswerten zeigt eine Sparkline den Jahresverlauf – bei zwanzig Zeilen zwanzig " +
          "Verläufe, die sich untereinander vergleichen lassen.",
        "„Einfügen“ → „Sparklines“ → „Linie“ (oder „Säule“, „Gewinn/Verlust“). Im Dialog den " +
          "Datenbereich (etwa B2:M21) und den Positionsbereich (N2:N21) angeben – eine Zeile " +
          "Daten, eine Sparkline. Alle zusammen bilden eine Gruppe und werden gemeinsam " +
          "formatiert.",
        "Sparklines sind keine Formeln und keine Objekte: Sie liegen hinter dem Zellinhalt " +
          "und wachsen mit der Zeilenhöhe und Spaltenbreite.",
      ],
      keyPoints: [
        "Einfügen → Sparklines → Linie / Säule / Gewinn/Verlust.",
        "Datenbereich und Positionsbereich – eine Zeile, eine Sparkline.",
        "Eine Gruppe wird gemeinsam formatiert.",
      ],
      task: {
        instruction:
          "Lege für mindestens drei Zeilen mit je sechs oder mehr Werten Sparklines in einer Spalte daneben an.",
        hint: "Werte markieren, Einfügen → Sparklines → Linie, Positionsbereich die leere Spalte rechts daneben.",
        macro: MAKRO_SPARKLINES,
        check: () =>
          pruefeMitMakro(MAKRO_SPARKLINES, (bericht) => {
            const gruppen = [...bericht.gruppe("gruppe").values()];
            const anzahl = gruppen.reduce((summe, gruppe) => summe + Number(gruppe.get("anzahl") ?? 0), 0);
            const erste = gruppen[0];
            return fromCriteria(
              [
                criterion(
                  erste
                    ? `${anzahl} Sparklines (${SPARKLINE_TYP[Number(erste.get("typ"))] ?? "?"}) in ${erste.get("blatt")}!${erste.get("ort")}`
                    : "Mindestens drei Sparklines",
                  anzahl >= 3,
                ),
              ],
              {
                success: "Ein Verlauf je Zeile – vergleichbar auf einen Blick.",
                failure: "Es gibt noch nicht genug Sparklines.",
                hint: "Den Positionsbereich so groß wählen wie die Zahl der Datenzeilen.",
              },
            );
          }),
      },
    },
    {
      id: "hoch-tief",
      title: "Höchst- und Tiefstwert markieren",
      explanation: [
        "Die Registerkarte „Sparkline“ erscheint, sobald eine Sparkline-Zelle markiert ist. " +
          "Unter „Anzeigen“ lassen sich Höchstpunkt, Tiefpunkt, negative Punkte sowie erster " +
          "und letzter Punkt einfärben.",
        "Höchst- und Tiefstwert sind fast immer die interessantesten: Wann war der beste " +
          "Monat, wann der schlechteste? Mit ihnen wird aus der Linie eine Aussage.",
        "Achtung bei der Achse: Standardmäßig skaliert jede Sparkline für sich. Sollen die " +
          "Zeilen vergleichbar sein, unter „Achse“ „Für alle Sparklines identisch“ wählen.",
      ],
      keyPoints: [
        "Sparkline → Anzeigen → Höchstpunkt, Tiefpunkt.",
        "Achse → Für alle Sparklines identisch, wenn Zeilen vergleichbar sein sollen.",
      ],
      task: {
        instruction: "Markiere in deinen Sparklines den Höchst- und den Tiefpunkt.",
        hint: "Sparkline-Zelle anklicken, Registerkarte Sparkline → Häkchen bei „Höchstpunkt“ und „Tiefpunkt“.",
        macro: MAKRO_SPARKLINES,
        check: () =>
          pruefeMitMakro(MAKRO_SPARKLINES, (bericht) => {
            const gruppen = [...bericht.gruppe("gruppe").values()];
            const markiert = gruppen.filter((gruppe) => gruppe.get("hoch") === "1" && gruppe.get("tief") === "1");
            return fromCriteria(
              [
                criterion("Sparklines vorhanden", gruppen.length > 0),
                criterion("Höchst- und Tiefpunkt sind markiert", markiert.length > 0),
              ],
              {
                success: "Bester und schlechtester Wert springen jetzt ins Auge.",
                failure: "Höchst- und Tiefpunkt sind noch nicht markiert.",
              },
            );
          }),
      },
    },
  ],
};
